import type {
  ConceptDiagram,
  ConceptDiagramNode,
  ConceptDiagramRole,
  ContentAsset,
  ContentBlock,
  Equation,
  InlineGlossaryEntry,
  Paper,
  SourceRef,
} from '../../../src/contracts/types.ts';
import { PROVENANCE_LABELS } from '../../../src/contracts/types.ts';
import {
  displayBlockText,
  displayBlockItems,
  ELI5_DESCRIPTION,
  ELI5_EYEBROW,
  ELI5_TITLE,
  isPublishableConceptDiagram,
  isPublishableReportOverview,
  isUnhelpfulEditorialNote,
  isLegacyOutline,
  isVisualSummaryText,
  originalFigureFor,
  paperMapItemsFromBlocks,
  REPORT_SECTION_HEADINGS,
  reportOverviewItems,
  splitReportBlocks,
  type VisualSummaryItem,
} from '../../../src/core/contentDisplay.ts';
import { NARROW_LAYOUT_MAX_WIDTH, clear, el, escapeHtml, svg } from './dom.ts';

/**
 * 本文ブロックの描画。
 *
 * 「原論文に記載」「外部資料で確認」「考察・応用案」は
 * 色・ラベル・アイコン・左罫の4つで区別する（色だけに依存しない）。
 */

const PROVENANCE_ICON: Record<ContentBlock['provenance'], string> = {
  paper: '📄',
  external: '🌐',
  editorial: '✍️',
};

const CONFIDENCE_LABEL: Record<ContentBlock['confidence'], string> = {
  confirmed: '',
  unverified: '未確認',
  needs_check: '要確認',
};

function summaryIcon(index: number): SVGElement {
  const icon = svg('svg', { viewBox: '0 0 48 48', 'aria-hidden': 'true' });
  icon.appendChild(svg('circle', { cx: 24, cy: 24, r: 17, fill: 'none', stroke: 'currentColor', 'stroke-width': 1.5 }));
  const paths = [
    'M15 29l6-11 5 8 3-5 4 8M15 33h18',
    'M19 29c-5-5-2-14 5-14s10 9 5 14M20 33h8M21 37h6',
    'M17 16h14M20 16v8l-5 9h18l-5-9v-8M19 29h10',
    'M15 32V20M22 32V25M29 32V16M14 34h20',
    'M24 14l11 5v7c0 7-5 11-11 13-6-2-11-6-11-13v-7zM18 26h12',
  ];
  icon.appendChild(svg('path', {
    d: paths[index % paths.length]!,
    fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round',
  }));
  return icon;
}

export function renderVisualSummary(items: VisualSummaryItem[]): HTMLElement {
  const figure = el('figure', {
    class: 'visual-summary',
    'aria-label': items.map((item) => `${item.label}。${item.text}`).join('。'),
  });
  figure.appendChild(
    el(
      'div',
      { class: 'visual-summary__head' },
      el('span', { class: 'visual-summary__eyebrow' }, ELI5_EYEBROW),
      el('h2', {}, ELI5_TITLE),
      el('p', {}, ELI5_DESCRIPTION),
    ),
  );
  const flow = el('div', { class: 'visual-summary__flow' });
  items.forEach((item, index) => {
    flow.appendChild(
      el(
        'div',
        { class: `visual-summary__item${index === items.length - 1 ? ' visual-summary__item--last' : ''}` },
        el('div', { class: 'visual-summary__icon' }, summaryIcon(index)),
        el('div', {}, el('span', { class: 'visual-summary__label' }, item.label), el('p', {}, item.text)),
      ),
    );
  });
  figure.appendChild(flow);
  return figure;
}

export function renderPaperMap(blocks: ContentBlock[]): HTMLElement | null {
  const items = paperMapItemsFromBlocks(blocks);
  if (items.length === 0) return null;
  const figure = el('figure', {
    class: 'paper-map',
    'aria-labelledby': 'paper-map-title',
  });
  figure.appendChild(
    el(
      'figcaption',
      { class: 'paper-map__head' },
      el('span', { class: 'visual-summary__eyebrow' }, 'KEY POINTS'),
      // 「論文全体を1枚で」と名乗ると論文固有の図（概念図）と誤認される。これは本文7節の要点を並べたカード。
      el('h2', { id: 'paper-map-title' }, '本文の要点マップ'),
      el('p', {}, '本文の各節の要点を、必要性から応用まで一覧にしたものです。各カードから詳しい節へ移動できます。'),
    ),
  );
  const flow = el('ol', { class: 'paper-map__flow' });
  for (const item of items) {
    flow.appendChild(
      el(
        'li',
        { class: `paper-map__item paper-map__item--${item.key}` },
        el(
          'a',
          { href: `#section-${item.headingId}` },
          el('span', { class: 'paper-map__label' }, item.label),
          el('p', {}, item.text),
          el(
            'span',
            { class: 'paper-map__source' },
            `${PROVENANCE_LABELS[item.provenance]}${CONFIDENCE_LABEL[item.confidence] ? `・${CONFIDENCE_LABEL[item.confidence]}` : ''}`,
          ),
        ),
      ),
    );
  }
  figure.appendChild(flow);
  return figure;
}

// ---------------------------------------------------------------------------
// 概念図（論文固有の仕組み）
// ---------------------------------------------------------------------------

/**
 * 概念図の配置。
 *
 * 描画（SVG）と配置の計算を分け、配置は DOM を使わない純関数にしてある。
 * 同じ図からは常に同じ座標が出る（乱数・画面幅を使わない。向きは引数で受け取る）。
 * 配置のしきい値ではなく見た目の寸法なので、config ではなくここに置く。
 */
export type ConceptDiagramOrientation = 'horizontal' | 'vertical';

export interface ConceptLayoutNode {
  id: string;
  role: ConceptDiagramRole;
  x: number;
  y: number;
  width: number;
  height: number;
  /** flow は役割の層、compare は列（0＝左、1＝右）、cycle は円周上の順番。 */
  layer: number;
  /** ラベルの行分け。 */
  lines: string[];
}

export interface ConceptLayoutEdge {
  from: string;
  to: string;
  label?: string;
  points: Array<{ x: number; y: number }>;
  labelAt: { x: number; y: number };
}

export interface ConceptLayout {
  orientation: ConceptDiagramOrientation;
  width: number;
  height: number;
  nodes: ConceptLayoutNode[];
  edges: ConceptLayoutEdge[];
}

/** flow の層。入力 → 処理 → 結果／根拠 → 比較 → 限界の順で左（上）から並べる。 */
const CONCEPT_ROLE_ORDER: Record<ConceptDiagramRole, number> = {
  input: 0,
  process: 1,
  output: 2,
  evidence: 2,
  comparison: 3,
  limitation: 4,
};

/** 役割の日本語。色だけに頼らず、ノードの中と凡例に必ず書く。 */
export const CONCEPT_ROLE_LABELS: Record<ConceptDiagramRole, string> = {
  input: '入力・前提',
  process: '処理・手法',
  output: '結果',
  evidence: '根拠',
  comparison: '比較対象',
  limitation: '限界',
};

const CONCEPT_GEOMETRY: Record<
  ConceptDiagramOrientation,
  { nodeWidth: number; charsPerLine: number; layerGap: number; nodeGap: number; margin: number }
> = {
  horizontal: { nodeWidth: 176, charsPerLine: 12, layerGap: 56, nodeGap: 16, margin: 16 },
  vertical: { nodeWidth: 168, charsPerLine: 11, layerGap: 48, nodeGap: 14, margin: 12 },
};
const CONCEPT_LINE_HEIGHT = 15;
/** 辺ラベルの文字の大きさ（styles.css の .concept-diagram__edge-label と同じ）。 */
const CONCEPT_EDGE_LABEL_FONT = 9.5;
/** 役割ラベルの行と上下の余白。ノードの高さ＝これ＋行数×行の高さ。 */
const CONCEPT_NODE_CHROME = 38;
/** ラベルの行数の上限。config の maxLabelCharacters（24）は 11 文字/行でも 3 行に収まる。 */
const CONCEPT_MAX_LINES = 3;
/** 横並びの図が器の幅に対してこれより縮むなら縦に積み替える。縮み過ぎると文字が読めない。 */
const CONCEPT_MIN_SCALE = 0.8;
/** 縦配置で1つの層に横に並べる上限。3 つ以上を並べると 390px の端末で文字が潰れる。 */
const CONCEPT_VERTICAL_COLUMNS = 2;

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * ラベルの行分け。空白があればそこで、無ければ文字数で切る。
 * 上限を超える分は最終行を「…」で切る（完全なラベルは SVG の title に残す）。
 */
export function wrapConceptLabel(text: string, charsPerLine: number, maxLines = CONCEPT_MAX_LINES): string[] {
  const lines: string[] = [];
  let current = '';
  for (const word of text.trim().split(/\s+/).filter(Boolean)) {
    const chars = Array.from(word);
    if (chars.length > charsPerLine) {
      if (current) lines.push(current);
      for (let index = 0; index < chars.length; index += charsPerLine) {
        lines.push(chars.slice(index, index + charsPerLine).join(''));
      }
      current = lines.pop() ?? '';
      continue;
    }
    const candidate = current ? `${current} ${word}` : word;
    if (Array.from(candidate).length <= charsPerLine) current = candidate;
    else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  if (lines.length === 0) return [''];
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  const last = Array.from(kept[maxLines - 1] ?? '');
  kept[maxLines - 1] = `${last.slice(0, Math.max(1, charsPerLine - 1)).join('')}…`;
  return kept;
}

interface ConceptPlacement {
  index: number;
  x: number;
  y: number;
  layer: number;
}

type ConceptGeometry = (typeof CONCEPT_GEOMETRY)[ConceptDiagramOrientation];

function placeFlow(
  nodes: ConceptDiagramNode[],
  orientation: ConceptDiagramOrientation,
  geometry: ConceptGeometry,
  nodeHeight: number,
  stackedRoleOrders: ReadonlySet<number> = new Set(),
): { placements: ConceptPlacement[]; width: number; height: number } {
  const { nodeWidth: w, layerGap, nodeGap, margin } = geometry;
  const layerValues = Array.from(new Set(nodes.map((node) => CONCEPT_ROLE_ORDER[node.role]))).sort((a, b) => a - b);
  const layers = layerValues.map((value) =>
    nodes.map((node, index) => ({ node, index })).filter(({ node }) => CONCEPT_ROLE_ORDER[node.role] === value).map(({ index }) => index),
  );
  const placements: ConceptPlacement[] = [];

  if (orientation === 'horizontal') {
    const layerHeights = layers.map((ids) => ids.length * nodeHeight + (ids.length - 1) * nodeGap);
    const contentHeight = Math.max(...layerHeights);
    layers.forEach((ids, layer) => {
      const x = margin + layer * (w + layerGap) + w / 2;
      const top = margin + (contentHeight - (layerHeights[layer] ?? 0)) / 2;
      ids.forEach((index, row) => {
        placements.push({ index, layer, x, y: top + row * (nodeHeight + nodeGap) + nodeHeight / 2 });
      });
    });
    return {
      placements,
      width: 2 * margin + layers.length * w + (layers.length - 1) * layerGap,
      height: 2 * margin + contentHeight,
    };
  }

  const columns = Math.min(CONCEPT_VERTICAL_COLUMNS, Math.max(...layers.map((ids) => ids.length)));
  const width = 2 * margin + columns * w + (columns - 1) * nodeGap;
  let cursor = margin;
  layers.forEach((ids, layer) => {
    // 層の中にラベル付きの辺がある層は1列に積む。横に並べると2つの箱の隙間（nodeGap）にラベルが入らず、
    // 箱に隠れる（実測 2026-09-25: 縦配置で278図）。1列なら箱の横の空き（キャンバスは2列分の幅のまま）を通せる。
    const layerColumns = stackedRoleOrders.has(layerValues[layer]!) ? 1 : columns;
    const rows = Math.ceil(ids.length / layerColumns);
    ids.forEach((index, position) => {
      const row = Math.floor(position / layerColumns);
      const column = position % layerColumns;
      const nodesInRow = Math.min(layerColumns, ids.length - row * layerColumns);
      placements.push({
        index,
        layer,
        x: width / 2 + (column - (nodesInRow - 1) / 2) * (w + nodeGap),
        y: cursor + row * (nodeHeight + nodeGap) + nodeHeight / 2,
      });
    });
    cursor += rows * nodeHeight + (rows - 1) * nodeGap + layerGap;
  });
  return { placements, width, height: cursor - layerGap + margin };
}

/** 比較は左右2列。偶数番目を左、奇数番目を右に置くので、生成側が対にして並べれば行が揃う。 */
function placeCompare(
  nodes: ConceptDiagramNode[],
  geometry: ConceptGeometry,
  nodeHeight: number,
): { placements: ConceptPlacement[]; width: number; height: number } {
  const { nodeWidth: w, layerGap, nodeGap, margin } = geometry;
  const columns = Math.min(2, nodes.length);
  const rows = Math.ceil(nodes.length / 2);
  const placements = nodes.map((_, index) => {
    const column = index % 2;
    const row = Math.floor(index / 2);
    return {
      index,
      layer: column,
      x: margin + column * (w + layerGap) + w / 2,
      y: margin + row * (nodeHeight + nodeGap) + nodeHeight / 2,
    };
  });
  return {
    placements,
    width: 2 * margin + columns * w + (columns - 1) * layerGap,
    height: 2 * margin + rows * nodeHeight + (rows - 1) * nodeGap,
  };
}

/** 循環は円周に等間隔。角度は並び順から決め、上（-90°）から時計回り。 */
function placeCycle(
  nodes: ConceptDiagramNode[],
  geometry: ConceptGeometry,
  nodeHeight: number,
  labelAllowance = 0,
): { placements: ConceptPlacement[]; width: number; height: number } {
  const { nodeWidth: w, nodeGap, margin } = geometry;
  const count = nodes.length;
  // 隣り合うノードの中心距離（弦）が対角線以上なら、矩形どうしは重ならない。辺ラベルがあれば、
  // 弦の上にラベルを置ける分だけ広げる（狭いとラベルが端の箱に重なる）。
  const chord = Math.hypot(w + nodeGap, nodeHeight + nodeGap) + labelAllowance;
  const radius = count <= 1 ? 0 : chord / (2 * Math.sin(Math.PI / count));
  const cx = margin + radius + w / 2;
  const cy = margin + radius + nodeHeight / 2;
  const placements = nodes.map((_, index) => {
    const angle = -Math.PI / 2 + (index * 2 * Math.PI) / count;
    return { index, layer: index, x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
  });
  return { placements, width: 2 * (margin + radius) + w, height: 2 * (margin + radius) + nodeHeight };
}

interface ConceptRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** 矩形の境界上で target へ向かう点。 */
function conceptBoundary(node: ConceptRect, target: { x: number; y: number }): { x: number; y: number } {
  const dx = target.x - node.x;
  const dy = target.y - node.y;
  const scale = 1 / Math.max(Math.abs(dx) / (node.width / 2), Math.abs(dy) / (node.height / 2), 1);
  return { x: node.x + dx * scale, y: node.y + dy * scale };
}

/** 重複点と、縦横に並んだ3点の真ん中（折れていない角）を落とす。同じ行の辺は直線になる。 */
function dedupe(points: Array<{ x: number; y: number }>): Array<{ x: number; y: number }> {
  const result: Array<{ x: number; y: number }> = [];
  for (const point of points) {
    const last = result[result.length - 1];
    if (last && Math.abs(last.x - point.x) < 1e-6 && Math.abs(last.y - point.y) < 1e-6) continue;
    const beforeLast = result[result.length - 2];
    if (
      last &&
      beforeLast &&
      ((Math.abs(beforeLast.y - last.y) < 1e-6 && Math.abs(last.y - point.y) < 1e-6) ||
        (Math.abs(beforeLast.x - last.x) < 1e-6 && Math.abs(last.x - point.x) < 1e-6))
    ) {
      result.pop();
    }
    result.push(point);
  }
  return result;
}

/** cycle の辺は円周上の2つの箱を結ぶ直線（箱の縁から縁まで）。 */
function cycleEdge(from: ConceptLayoutNode, to: ConceptLayoutNode): ConceptPoint[] {
  return [conceptBoundary(from, to), conceptBoundary(to, from)];
}

/**
 * 概念図の座標を決める。DOM を使わない純関数（tests/webBlocks.test.ts で検証）。
 *
 * - flow    … 役割の順に層を作り、横配置は左→右、縦配置は上→下に並べる。同じ層は上から（左から）順。
 * - compare … 左右2列。縦配置でも2列のまま（比較の意味を保つ）で、ノード幅だけ縮める。
 * - cycle   … 円周に等間隔。
 */
export function layoutConceptDiagram(diagram: ConceptDiagram, orientation: ConceptDiagramOrientation): ConceptLayout {
  const base = CONCEPT_GEOMETRY[orientation];
  const labelWidths = diagram.edges.map((edge) => (edge.label ? edgeLabelWidth(edge.label) : 0));
  // 横に並べる図（flow の横配置・compare）は、層と層の隙間に辺ラベルを置く。いちばん長いラベルが
  // 隙間に収まる幅まで広げ、ラベルがノードの箱にかからないようにする。
  const widensGap = (diagram.kind === 'flow' && orientation === 'horizontal') || diagram.kind === 'compare';
  const geometry = widensGap
    ? { ...base, layerGap: Math.max(base.layerGap, Math.ceil(Math.max(0, ...labelWidths)) + 2 * (CONCEPT_LABEL_CLEARANCE + CONCEPT_TRACK_STEP)) }
    : base;
  const wrapped = diagram.nodes.map((node) => wrapConceptLabel(node.label, geometry.charsPerLine));
  const maxLines = Math.max(1, ...wrapped.map((lines) => lines.length));
  const nodeHeight = CONCEPT_NODE_CHROME + maxLines * CONCEPT_LINE_HEIGHT;
  if (diagram.nodes.length === 0) {
    return { orientation, width: 2 * geometry.margin, height: 2 * geometry.margin, nodes: [], edges: [] };
  }

  const placed =
    diagram.kind === 'cycle'
      ? placeCycle(diagram.nodes, geometry, nodeHeight,
        Math.max(0, ...labelWidths) > 0 ? Math.max(...labelWidths) + 2 * CONCEPT_LABEL_CLEARANCE : 0)
      : diagram.kind === 'compare'
        ? placeCompare(diagram.nodes, geometry, nodeHeight)
        : placeFlow(diagram.nodes, orientation, geometry, nodeHeight, orientation === 'vertical' ? labeledInternalRoleOrders(diagram) : undefined);

  const nodes: ConceptLayoutNode[] = placed.placements
    .slice()
    .sort((a, b) => a.index - b.index)
    .map((placement) => {
      const node = diagram.nodes[placement.index]!;
      return {
        id: node.id,
        role: node.role,
        x: round2(placement.x),
        y: round2(placement.y),
        width: geometry.nodeWidth,
        height: nodeHeight,
        layer: placement.layer,
        lines: wrapped[placement.index] ?? [node.label],
      };
    });
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const router = conceptRouter(diagram.kind, orientation, geometry, nodes);
  const edges: ConceptLayoutEdge[] = [];
  for (const edge of diagram.edges) {
    const from = byId.get(edge.from);
    const to = byId.get(edge.to);
    if (!from || !to || from === to) continue;
    const points = router(from, to, edge.label ? edgeLabelWidth(edge.label) : 0).map((point) => ({ x: round2(point.x), y: round2(point.y) }));
    edges.push({
      from: edge.from,
      to: edge.to,
      ...(edge.label ? { label: edge.label } : {}),
      points,
      labelAt: points[0]!,
    });
  }
  if (diagram.kind !== 'cycle') {
    separateConceptEdges(edges, nodes);
    resolveConceptOverlaps(edges, nodes);
  } else {
    separateParallelChords(edges, byId);
  }
  placeEdgeLabels(edges, nodes);
  return fitConceptCanvas({ orientation, width: round2(placed.width), height: round2(placed.height), nodes, edges }, geometry.margin);
}

type ConceptPoint = { x: number; y: number };

/** 同じ役割（＝同じ層）のノードどうしを結ぶラベル付きの辺がある層（役割の順位）。 */
function labeledInternalRoleOrders(diagram: ConceptDiagram): Set<number> {
  const roleOf = new Map(diagram.nodes.map((node) => [node.id, CONCEPT_ROLE_ORDER[node.role]]));
  const orders = new Set<number>();
  for (const edge of diagram.edges) {
    const from = roleOf.get(edge.from);
    if (edge.label && from !== undefined && from === roleOf.get(edge.to)) orders.add(from);
  }
  return orders;
}

interface ConceptBox {
  id?: string;
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** ラベルの箱とノード・他のラベルとの間に空ける余白。 */
const CONCEPT_LABEL_CLEARANCE = 6;
/** 図の外側に作る迂回路の間隔（縦配置は 390px 端末の幅に収めるため狭くする）。 */
const CONCEPT_LANE_STEP: Record<ConceptDiagramOrientation, number> = { horizontal: 10, vertical: 6 };

function edgeLabelWidth(label: string): number {
  return [...label].reduce((sum, char) => sum + (char.charCodeAt(0) < 0x80 ? 0.6 : 1) * CONCEPT_EDGE_LABEL_FONT, 0);
}

function conceptBoxOf(node: ConceptLayoutNode): ConceptBox {
  return { id: node.id, left: node.x - node.width / 2, right: node.x + node.width / 2, top: node.y - node.height / 2, bottom: node.y + node.height / 2 };
}

/** 線分が箱の内部を通るか。辺は縦横の折れ線（cycle だけ斜め）なので、斜めは細かく刻んで確かめる。 */
function segmentCrossesBox(a: ConceptPoint, b: ConceptPoint, box: ConceptBox): boolean {
  const eps = 0.5;
  const inside = (x: number, y: number): boolean =>
    x > box.left + eps && x < box.right - eps && y > box.top + eps && y < box.bottom - eps;
  if (Math.abs(a.x - b.x) < 1e-6) {
    if (a.x <= box.left + eps || a.x >= box.right - eps) return false;
    return Math.max(a.y, b.y) > box.top + eps && Math.min(a.y, b.y) < box.bottom - eps;
  }
  if (Math.abs(a.y - b.y) < 1e-6) {
    if (a.y <= box.top + eps || a.y >= box.bottom - eps) return false;
    return Math.max(a.x, b.x) > box.left + eps && Math.min(a.x, b.x) < box.right - eps;
  }
  for (let step = 1; step < 64; step += 1) {
    const t = step / 64;
    if (inside(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t)) return true;
  }
  return false;
}

/** 両端以外の箱から、線が空ける最小の距離。縁をかすめると、線が箱の枠と一体に見える。 */
const CONCEPT_ROUTE_CLEARANCE = 3;

/** 辺が両端以外のノードの箱を通るか（箱の周りの余白を含む）。通ると、別のノードから出た関係に見えてしまう。 */
export function conceptRouteCrossesNodes(points: ConceptPoint[], nodes: ConceptLayoutNode[], fromId: string, toId: string): boolean {
  return nodes.some((node) => {
    if (node.id === fromId || node.id === toId) return false;
    const box = conceptBoxOf(node);
    const padded = {
      left: box.left - CONCEPT_ROUTE_CLEARANCE, right: box.right + CONCEPT_ROUTE_CLEARANCE,
      top: box.top - CONCEPT_ROUTE_CLEARANCE, bottom: box.bottom + CONCEPT_ROUTE_CLEARANCE,
    };
    return points.slice(1).some((point, index) => segmentCrossesBox(points[index]!, point, padded));
  });
}

/**
 * 辺の経路を決める関数を作る。
 *
 * 辺が途中のノードの箱を通ると、どのノードからの関係なのかを取り違える
 * （実測 2026-09-25: flow 945図のうち横116図・縦121図で、層を飛ばす辺や逆向きの辺が途中の箱を突き抜けていた）。
 * - 横に層を並べる図（flow 横配置・compare）は、層と層の隙間（ノードの無い帯）だけを縦に通る。隣の層・同じ層・
 *   1つ前の層への辺はこれだけで箱を通らない。2層以上離れた辺は、隙間を使う経路が塞がれていれば図の上下の外側を回る。
 * - 縦に層を積む図（flow 縦配置）は、行と行の隙間を横に通り、塞がれていれば左右の外側を縦に回る。
 * - cycle は円周上の直線のまま（従来どおり）。
 */
function conceptRouter(
  kind: ConceptDiagram['kind'],
  orientation: ConceptDiagramOrientation,
  geometry: ConceptGeometry,
  nodes: ConceptLayoutNode[],
): (from: ConceptLayoutNode, to: ConceptLayoutNode, labelWidth: number) => ConceptPoint[] {
  const boxes = nodes.map(conceptBoxOf);
  const minX = Math.min(...boxes.map((box) => box.left));
  const maxX = Math.max(...boxes.map((box) => box.right));
  const minY = Math.min(...boxes.map((box) => box.top));
  const maxY = Math.max(...boxes.map((box) => box.bottom));
  const lanes = { before: 0, after: 0 };
  const step = CONCEPT_LANE_STEP[orientation];
  // 経路は、途中の箱を通らず、ラベル付きなら箱に重ならずにラベルを置ける場所がある場合だけ使う。
  const valid = (points: ConceptPoint[], from: ConceptLayoutNode, to: ConceptLayoutNode, labelWidth = 0): boolean =>
    !conceptRouteCrossesNodes(points, nodes, from.id, to.id)
    && (labelWidth === 0 || labelCandidates(points, labelWidth).some((point) =>
      boxes.every((box) => !boxesOverlap(labelBoxAt(point, labelWidth), box))));

  if (kind === 'cycle') {
    // 円周上の直線（弦）が途中の箱を横切るなら、輪の外側へふくらませて回る。
    const center = {
      x: nodes.reduce((sum, node) => sum + node.x, 0) / nodes.length,
      y: nodes.reduce((sum, node) => sum + node.y, 0) / nodes.length,
    };
    const radius = Math.max(...nodes.map((node) => Math.hypot(node.x - center.x, node.y - center.y)));
    const reach = Math.max(geometry.nodeWidth, nodes[0]!.height) / 2 + 16;
    return (from, to, labelWidth) => {
      const chord = cycleEdge(from, to);
      if (valid(chord, from, to, labelWidth)) return chord;
      const angleOf = (node: ConceptLayoutNode): number => Math.atan2(node.y - center.y, node.x - center.x);
      const a = angleOf(from);
      let delta = angleOf(to) - a;
      while (delta <= -Math.PI) delta += 2 * Math.PI;
      while (delta > Math.PI) delta -= 2 * Math.PI;
      for (const side of [1, -1]) {
        // 短い弧の側（side=1）を先に、だめなら反対側を回る。
        const mid = a + (side === 1 ? delta / 2 : delta / 2 + Math.PI);
        for (const scale of [1, 1.5, 2, 3]) {
          const bend = { x: center.x + (radius + reach * scale) * Math.cos(mid), y: center.y + (radius + reach * scale) * Math.sin(mid) };
          const points = [conceptBoundary(from, bend), bend, conceptBoundary(to, bend)];
          if (valid(points, from, to, labelWidth)) return points;
        }
      }
      return chord;
    };
  }

  const layers = Math.max(...nodes.map((node) => node.layer)) + 1;
  const extent = (layer: number, pick: (box: ConceptBox) => number, reduce: (...values: number[]) => number): number =>
    reduce(...boxes.filter((_, index) => nodes[index]!.layer === layer).map(pick));

  if (kind === 'compare' || orientation === 'horizontal') {
    const left = Array.from({ length: layers }, (_, layer) => extent(layer, (box) => box.left, Math.min));
    const right = Array.from({ length: layers }, (_, layer) => extent(layer, (box) => box.right, Math.max));
    const outer = geometry.layerGap / 2;
    const gapAfter = (layer: number): number => (layer < layers - 1 ? (right[layer]! + left[layer + 1]!) / 2 : right[layer]! + outer);
    const gapBefore = (layer: number): number => (layer > 0 ? (right[layer - 1]! + left[layer]!) / 2 : left[layer]! - outer);
    return (from, to, labelWidth) => {
      const fromRight = from.x + from.width / 2;
      const fromLeft = from.x - from.width / 2;
      const toRight = to.x + to.width / 2;
      const toLeft = to.x - to.width / 2;
      const d = to.layer - from.layer;
      const candidates: ConceptPoint[][] = [];
      if (d >= 1) {
        const gap = gapAfter(from.layer);
        candidates.push([{ x: fromRight, y: from.y }, { x: gap, y: from.y }, { x: gap, y: to.y }, { x: toLeft, y: to.y }]);
        if (d >= 2) {
          const before = gapBefore(to.layer);
          candidates.push([{ x: fromRight, y: from.y }, { x: before, y: from.y }, { x: before, y: to.y }, { x: toLeft, y: to.y }]);
        }
      } else if (d <= -1) {
        const gap = gapBefore(from.layer);
        candidates.push([{ x: fromLeft, y: from.y }, { x: gap, y: from.y }, { x: gap, y: to.y }, { x: toRight, y: to.y }]);
        if (d <= -2) {
          const after = gapAfter(to.layer);
          candidates.push([{ x: fromLeft, y: from.y }, { x: after, y: from.y }, { x: after, y: to.y }, { x: toRight, y: to.y }]);
        }
      } else {
        // ラベルの無い辺は、間に箱が無ければ直線（隣り合う箱）。ラベル付きは直線の隙間に文字が入らないので、
        // 隣の隙間で折り返す。層と層の隙間があればそちらを使う。
        if (labelWidth === 0) candidates.push([conceptBoundary(from, to), conceptBoundary(to, from)]);
        const useAfter = from.layer < layers - 1 || from.layer === 0;
        const gap = useAfter ? gapAfter(from.layer) : gapBefore(from.layer);
        candidates.push(useAfter
          ? [{ x: fromRight, y: from.y }, { x: gap, y: from.y }, { x: gap, y: to.y }, { x: toRight, y: to.y }]
          : [{ x: fromLeft, y: from.y }, { x: gap, y: from.y }, { x: gap, y: to.y }, { x: toLeft, y: to.y }]);
      }
      for (const candidate of candidates) {
        const points = dedupe(candidate);
        if (valid(points, from, to, labelWidth)) return points;
      }
      // 塞がれていれば、図の上下の外側の通り道を回る（通り道ごとに間隔を空けて重ねない）。
      const above = (from.y + to.y) / 2 <= (minY + maxY) / 2;
      const lane = above ? minY - step * ++lanes.before : maxY + step * ++lanes.after;
      const exit = d >= 0 ? gapAfter(from.layer) : gapBefore(from.layer);
      const enter = d >= 0 ? gapBefore(to.layer) : gapAfter(to.layer);
      return dedupe([
        { x: d >= 0 ? fromRight : fromLeft, y: from.y },
        { x: exit, y: from.y },
        { x: exit, y: lane },
        { x: enter, y: lane },
        { x: enter, y: to.y },
        { x: d >= 0 ? toLeft : toRight, y: to.y },
      ]);
    };
  }

  // flow の縦配置: 層は上から下へ積んだ帯。帯の中は最大2列で、行と行の間は隙間になる。
  const top = Array.from({ length: layers }, (_, layer) => extent(layer, (box) => box.top, Math.min));
  const bottom = Array.from({ length: layers }, (_, layer) => extent(layer, (box) => box.bottom, Math.max));
  const half = geometry.nodeGap / 2;
  const centerX = (minX + maxX) / 2;
  return (from, to, labelWidth) => {
    const fromTop = from.y - from.height / 2;
    const fromBottom = from.y + from.height / 2;
    const toTop = to.y - to.height / 2;
    const toBottom = to.y + to.height / 2;
    const candidates: ConceptPoint[][] = [];
    if (to.layer > from.layer) {
      const band = (bottom[from.layer]! + top[from.layer + 1]!) / 2;
      candidates.push([{ x: from.x, y: fromBottom }, { x: from.x, y: band }, { x: to.x, y: band }, { x: to.x, y: toTop }]);
      candidates.push([{ x: from.x, y: fromBottom }, { x: from.x, y: toTop - half }, { x: to.x, y: toTop - half }, { x: to.x, y: toTop }]);
    } else if (to.layer < from.layer) {
      const band = (top[from.layer]! + bottom[from.layer - 1]!) / 2;
      candidates.push([{ x: from.x, y: fromTop }, { x: from.x, y: band }, { x: to.x, y: band }, { x: to.x, y: toBottom }]);
    } else {
      // 同じ層: ラベルの無い辺は、間に箱が無ければ直線。ラベル付きは（1列に積んだ層なので）箱の横の空きを通す。
      if (labelWidth === 0) candidates.push([conceptBoundary(from, to), conceptBoundary(to, from)]);
      const side = Math.max(half, labelWidth / 2 + CONCEPT_LABEL_CLEARANCE);
      const x = Math.max(from.x + from.width / 2, to.x + to.width / 2) + side;
      candidates.push([
        { x: from.x + from.width / 2, y: from.y },
        { x, y: from.y },
        { x, y: to.y },
        { x: to.x + to.width / 2, y: to.y },
      ]);
    }
    for (const candidate of candidates) {
      const points = dedupe(candidate);
      if (valid(points, from, to, labelWidth)) return points;
    }
    // 塞がれていれば、行の隙間を横に出て、図の左右の外側を縦に回る。
    const leftSide = (from.x + to.x) / 2 <= centerX;
    const lane = leftSide ? minX - step * ++lanes.before : maxX + step * ++lanes.after;
    const down = to.y > from.y;
    const up = to.y < from.y;
    const start = up ? { x: from.x, y: fromTop } : { x: from.x, y: fromBottom };
    const exitY = up ? fromTop - half : fromBottom + half;
    const end = down ? { x: to.x, y: toTop } : { x: to.x, y: toBottom };
    const enterY = down ? toTop - half : toBottom + half;
    return dedupe([start, { x: from.x, y: exitY }, { x: lane, y: exitY }, { x: lane, y: enterY }, { x: to.x, y: enterY }, end]);
  };
}

/** 区間の上でラベルを試す位置（中央から外側へ）。同じ区間を共有する兄弟の辺にも空きが見つかる細かさにする。 */
const LABEL_POSITIONS = [0.5, 0.4, 0.6, 0.3, 0.7, 0.2, 0.8, 0.1, 0.9, 0.05, 0.95];

/** ラベルを置く候補点。経路の中ほどの区間から順に、区間の中央・その前後。 */
function labelCandidates(points: ConceptPoint[], labelWidth: number): ConceptPoint[] {
  const segments = points.slice(1).map((point, index) => ({ a: points[index]!, b: point, index }));
  const middle = (segments.length - 1) / 2;
  segments.sort((x, y) => Math.abs(x.index - middle) - Math.abs(y.index - middle)
    || Math.hypot(y.b.x - y.a.x, y.b.y - y.a.y) - Math.hypot(x.b.x - x.a.x, x.b.y - x.a.y) || x.index - y.index);
  const on = segments.flatMap(({ a, b }) => LABEL_POSITIONS
    .map((t) => ({ point: { x: round2(a.x + (b.x - a.x) * t), y: round2(a.y + (b.y - a.y) * t) }, vertical: Math.abs(a.x - b.x) < 1e-6 })));
  // 線の上に置けないときは、線の横（縦の区間）や上下（横の区間）も試し、それでもだめなら少しずつ離す。
  const aside = (steps: number[]) => on.flatMap(({ point, vertical }) => steps.flatMap((step) => (vertical
    ? [1, -1].map((sign) => ({ x: round2(point.x + sign * (labelWidth / 2 + 5 + step)), y: point.y }))
    : [{ x: point.x, y: round2(point.y + CONCEPT_EDGE_LABEL_FONT + 8 + step) }, { x: point.x, y: round2(point.y - 8 - step) }])));
  return [...on.map(({ point }) => point), ...aside([0]), ...aside([8, 16, 24])];
}

/** 同じ箱の同じ辺に付く線の間隔と、隙間を通る線どうしの間隔。 */
const CONCEPT_PORT_STEP = 10;
const CONCEPT_TRACK_STEP = 7;

type ConceptSide = 'left' | 'right' | 'top' | 'bottom';

function sideOf(point: ConceptPoint, box: ConceptBox): ConceptSide | undefined {
  if (Math.abs(point.x - box.left) < 0.5) return 'left';
  if (Math.abs(point.x - box.right) < 0.5) return 'right';
  if (Math.abs(point.y - box.top) < 0.5) return 'top';
  if (Math.abs(point.y - box.bottom) < 0.5) return 'bottom';
  return undefined;
}

/**
 * 線の重なりをほどく。出発点も到着点も違う辺の線が同じ直線上で重なると、どの箱どうしの関係か読めない
 * （実測 2026-09-25: flow 横配置の26%・縦配置の14%の図で起きていた）。
 * 1. 同じ箱の同じ辺に付く線は、辺に沿って間隔を空け、相手の位置の順に並べる（交差を減らす）。
 * 2. 隙間を通る区間（折れ線の途中の区間）は、範囲が重なる別の辺と違う通り道へ割り当てる。通り道は
 *    ノードの箱にかからない範囲でだけずらすので、箱を通らない性質は保たれる。決定論的。
 */
function separateConceptEdges(edges: ConceptLayoutEdge[], nodes: ConceptLayoutNode[]): void {
  const boxes = new Map(nodes.map((node) => [node.id, conceptBoxOf(node)]));
  const allBoxes = [...boxes.values()];

  // 1. 接続点。直線の辺（2点）は動かせないので位置を固定し、ほかの線をその両側へ振り分ける。
  type Port = { edge: ConceptLayoutEdge; end: 'start' | 'end'; side: ConceptSide; toward: number; fixed: boolean };
  const ports = new Map<string, Port[]>();
  for (const edge of edges) {
    for (const end of ['start', 'end'] as const) {
      const box = boxes.get(end === 'start' ? edge.from : edge.to)!;
      const point = end === 'start' ? edge.points[0]! : edge.points[edge.points.length - 1]!;
      const side = sideOf(point, box);
      if (!side) continue;
      const far = end === 'start' ? edge.points[edge.points.length - 1]! : edge.points[0]!;
      const key = `${end === 'start' ? edge.from : edge.to}:${side}`;
      const list = ports.get(key) ?? [];
      list.push({ edge, end, side, toward: side === 'left' || side === 'right' ? far.y : far.x, fixed: edge.points.length < 3 });
      ports.set(key, list);
    }
  }
  const before = new Map(edges.map((edge) => [edge, edge.points.map((point) => ({ ...point }))]));
  for (const [key, list] of ports) {
    if (list.length < 2 || list.every((port) => port.fixed)) continue;
    const box = boxes.get(key.slice(0, key.lastIndexOf(':')))!;
    const vertical = list[0]!.side === 'left' || list[0]!.side === 'right';
    const low = vertical ? box.top : box.left;
    const high = vertical ? box.bottom : box.right;
    const step = Math.min(CONCEPT_PORT_STEP, (high - low - 2 * CONCEPT_PORT_STEP) / (list.length - 1));
    const coordinate = (port: Port): number => {
      const point = port.end === 'start' ? port.edge.points[0]! : port.edge.points[port.edge.points.length - 1]!;
      return vertical ? point.y : point.x;
    };
    const fixed = list.find((port) => port.fixed);
    const anchor = fixed ? coordinate(fixed) : (low + high) / 2;
    const movable = list.filter((port) => !port.fixed).sort((a, b) => a.toward - b.toward
      || (a.edge.from < b.edge.from ? -1 : a.edge.from > b.edge.from ? 1 : 0) || (a.edge.to < b.edge.to ? -1 : a.edge.to > b.edge.to ? 1 : 0));
    let slots: number[];
    if (fixed) {
      // 固定した線より手前（相手が小さい側）へ向かう線は負の側、それ以外は正の側へ、近い順に並べる。
      const below = movable.filter((port) => port.toward < anchor).length;
      slots = movable.map((_, index) => (index < below ? -(below - index) : index - below + 1) * step);
    } else {
      slots = movable.map((_, index) => (index - (movable.length - 1) / 2) * step);
    }
    movable.forEach((port, index) => {
      const target = round2(Math.min(high - 4, Math.max(low + 4, anchor + slots[index]!)));
      const points = port.edge.points;
      const [i, j] = port.end === 'start' ? [0, 1] : [points.length - 1, points.length - 2];
      points[i] = vertical ? { ...points[i]!, y: target } : { ...points[i]!, x: target };
      points[j] = vertical ? { ...points[j]!, y: target } : { ...points[j]!, x: target };
    });
  }
  // ずらした結果、途中の箱を通るようになった辺は元に戻す（箱を通らないことを優先する）。
  for (const edge of edges) {
    if (conceptRouteCrossesNodes(edge.points, nodes, edge.from, edge.to)) edge.points = before.get(edge)!;
  }

  // 2. 通り道（折れ線の途中の区間）
  type Channel = { edge: ConceptLayoutEdge; index: number; vertical: boolean; at: number; from: number; to: number };
  const channels: Channel[] = [];
  for (const edge of edges) {
    for (let index = 1; index < edge.points.length - 2; index += 1) {
      const a = edge.points[index]!;
      const b = edge.points[index + 1]!;
      const vertical = Math.abs(a.x - b.x) < 1e-6;
      if (!vertical && Math.abs(a.y - b.y) >= 1e-6) continue;
      channels.push({
        edge, index, vertical, at: vertical ? a.x : a.y,
        from: vertical ? Math.min(a.y, b.y) : Math.min(a.x, b.x),
        to: vertical ? Math.max(a.y, b.y) : Math.max(a.x, b.x),
      });
    }
  }
  const groups = new Map<string, Channel[]>();
  for (const channel of channels) {
    const key = `${channel.vertical ? 'v' : 'h'}:${round2(channel.at)}`;
    groups.set(key, [...(groups.get(key) ?? []), channel]);
  }
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    group.sort((a, b) => a.from - b.from || a.to - b.to || edges.indexOf(a.edge) - edges.indexOf(b.edge));
    const tracks: Channel[][] = [];
    for (const channel of group) {
      // 出発点や到着点を共有する辺でも、線を重ねると関係の数が読めなくなるので通り道を分ける。
      let track = tracks.findIndex((members) => members.every((other) =>
        other.to + 2 < channel.from || channel.to + 2 < other.from));
      if (track < 0) {
        track = tracks.length;
        tracks.push([]);
      }
      tracks[track]!.push(channel);
      if (track === 0) continue;
      const offset = Math.ceil(track / 2) * CONCEPT_TRACK_STEP * (track % 2 === 1 ? 1 : -1);
      // 区間の範囲で、ノードの箱にかからない位置までに限ってずらす。
      const blocking = allBoxes.filter((box) => channel.vertical
        ? box.bottom > channel.from && box.top < channel.to
        : box.right > channel.from && box.left < channel.to);
      const low = Math.max(-Infinity, ...blocking.map((box) => (channel.vertical ? box.right : box.bottom)).filter((edge) => edge <= channel.at));
      const high = Math.min(Infinity, ...blocking.map((box) => (channel.vertical ? box.left : box.top)).filter((edge) => edge >= channel.at));
      const next = round2(Math.min(high - CONCEPT_ROUTE_CLEARANCE, Math.max(low + CONCEPT_ROUTE_CLEARANCE, channel.at + offset)));
      const points = channel.edge.points;
      const saved = points.map((point) => ({ ...point }));
      for (const index of [channel.index, channel.index + 1]) {
        points[index] = channel.vertical ? { ...points[index]!, x: next } : { ...points[index]!, y: next };
      }
      if (conceptRouteCrossesNodes(points, nodes, channel.edge.from, channel.edge.to)) channel.edge.points = saved;
    }
  }
}

/**
 * cycle で同じ2つの箱を結ぶ弦（行きと帰り、重複）を平行にずらす。同じ線に重なると、どちら向きの関係か、
 * ラベルがどちらの線のものか読めない。ずらした線と箱の縁の交点を求め直し、端は箱の縁に付けたままにする。
 */
function separateParallelChords(edges: ConceptLayoutEdge[], byId: Map<string, ConceptLayoutNode>): void {
  const groups = new Map<string, ConceptLayoutEdge[]>();
  for (const edge of edges) {
    if (edge.points.length !== 2) continue;
    const key = [edge.from, edge.to].sort().join('|');
    groups.set(key, [...(groups.get(key) ?? []), edge]);
  }
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    const [first, second] = [group[0]!.from, group[0]!.to].sort() as [string, string];
    const a = byId.get(first)!;
    const b = byId.get(second)!;
    const length = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const normal = { x: -(b.y - a.y) / length, y: (b.x - a.x) / length };
    group.forEach((edge, index) => {
      const offset = (index - (group.length - 1) / 2) * CONCEPT_PORT_STEP;
      const from = byId.get(edge.from)!;
      const to = byId.get(edge.to)!;
      const origin = { x: from.x + normal.x * offset, y: from.y + normal.y * offset };
      const direction = { x: to.x - from.x, y: to.y - from.y };
      const [, exit] = slab(origin, direction, conceptBoxOf(from));
      const [enter] = slab(origin, direction, conceptBoxOf(to));
      edge.points = [
        { x: round2(origin.x + direction.x * exit), y: round2(origin.y + direction.y * exit) },
        { x: round2(origin.x + direction.x * enter), y: round2(origin.y + direction.y * enter) },
      ];
    });
  }
}

/** 半直線 origin + t·direction が箱に入る t と出る t（スラブ法）。 */
function slab(origin: ConceptPoint, direction: ConceptPoint, box: ConceptBox): [number, number] {
  let enter = -Infinity;
  let exit = Infinity;
  for (const [o, d, low, high] of [[origin.x, direction.x, box.left, box.right], [origin.y, direction.y, box.top, box.bottom]] as const) {
    if (Math.abs(d) < 1e-9) continue;
    const t1 = (low - o) / d;
    const t2 = (high - o) / d;
    enter = Math.max(enter, Math.min(t1, t2));
    exit = Math.min(exit, Math.max(t1, t2));
  }
  return [enter, exit];
}

type ConceptSegment = { a: ConceptPoint; b: ConceptPoint };

function collinearOverlap(s: ConceptSegment, t: ConceptSegment): boolean {
  const vs = Math.abs(s.a.x - s.b.x) < 0.01;
  const vt = Math.abs(t.a.x - t.b.x) < 0.01;
  const hs = Math.abs(s.a.y - s.b.y) < 0.01;
  const ht = Math.abs(t.a.y - t.b.y) < 0.01;
  const shared = (p: [number, number], q: [number, number]): number =>
    Math.min(Math.max(...p), Math.max(...q)) - Math.max(Math.min(...p), Math.min(...q));
  if (vs && vt && Math.abs(s.a.x - t.a.x) < 0.01) return shared([s.a.y, s.b.y], [t.a.y, t.b.y]) > 1;
  if (hs && ht && Math.abs(s.a.y - t.a.y) < 0.01) return shared([s.a.x, s.b.x], [t.a.x, t.b.x]) > 1;
  return false;
}

/** 2本の辺が同じ直線上で重なる区間の数（出発点や到着点を共有する辺どうしも数える）。 */
function sharedLineOverlaps(edge: ConceptLayoutEdge, edges: ConceptLayoutEdge[]): number {
  const own = edge.points.slice(1).map((b, index) => ({ a: edge.points[index]!, b }));
  let count = 0;
  for (const other of edges) {
    if (other === edge) continue;
    const theirs = other.points.slice(1).map((b, index) => ({ a: other.points[index]!, b }));
    for (const s of own) for (const t of theirs) if (collinearOverlap(s, t)) count += 1;
  }
  return count;
}

/**
 * 仕上げ: まだ重なっている区間を、垂直方向へ少しずつずらして試す。途中の箱を通らず、接続点が箱の辺の中に
 * 収まり、重なりが減る場合だけ採用する。端の区間（箱に付く区間）は接続点ごと動かす。決定論的。
 */
function resolveConceptOverlaps(edges: ConceptLayoutEdge[], nodes: ConceptLayoutNode[]): void {
  const boxes = new Map(nodes.map((node) => [node.id, conceptBoxOf(node)]));
  const withinSide = (edge: ConceptLayoutEdge, points: ConceptPoint[]): boolean =>
    [[edge.from, points[0]!], [edge.to, points[points.length - 1]!]].every(([id, point]) => {
      const box = boxes.get(id as string)!;
      const p = point as ConceptPoint;
      const side = sideOf(p, box);
      if (side === 'left' || side === 'right') return p.y >= box.top + 4 && p.y <= box.bottom - 4;
      if (side === 'top' || side === 'bottom') return p.x >= box.left + 4 && p.x <= box.right - 4;
      return true;
    });
  for (let round = 0; round < 4; round += 1) {
    let changed = false;
    for (const edge of edges) {
      const current = sharedLineOverlaps(edge, edges);
      if (current === 0) continue;
      let improved = false;
      for (let index = 0; index < edge.points.length - 1 && !improved; index += 1) {
        const a = edge.points[index]!;
        const b = edge.points[index + 1]!;
        const vertical = Math.abs(a.x - b.x) < 1e-6;
        if (!vertical && Math.abs(a.y - b.y) >= 1e-6) continue;
        // 狭い隙間（箱と箱の間の数 px）にも2本目を通せるよう、細かい幅から試す。
        for (const delta of [CONCEPT_TRACK_STEP, -CONCEPT_TRACK_STEP, 4, -4, 10, -10, 2 * CONCEPT_TRACK_STEP, -2 * CONCEPT_TRACK_STEP, 21, -21]) {
          const moved = edge.points.map((point, at) => (at === index || at === index + 1
            ? (vertical ? { ...point, x: round2(point.x + delta) } : { ...point, y: round2(point.y + delta) })
            : point));
          const trial = { ...edge, points: moved };
          if (conceptRouteCrossesNodes(moved, nodes, edge.from, edge.to) || !withinSide(edge, moved)) continue;
          if (sharedLineOverlaps(trial, edges.map((other) => (other === edge ? trial : other))) >= current) continue;
          edge.points = moved;
          improved = true;
          changed = true;
          break;
        }
      }
    }
    if (!changed) break;
  }
}

function labelBoxAt(point: ConceptPoint, width: number): ConceptBox {
  // 文字は labelAt.y - 4 を基線に、中央揃えで描く（conceptCanvas）。
  return {
    left: point.x - width / 2 - CONCEPT_LABEL_CLEARANCE / 2,
    right: point.x + width / 2 + CONCEPT_LABEL_CLEARANCE / 2,
    top: point.y - 4 - CONCEPT_EDGE_LABEL_FONT - CONCEPT_LABEL_CLEARANCE / 2,
    bottom: point.y - 4 + 3 + CONCEPT_LABEL_CLEARANCE / 2,
  };
}

function boxesOverlap(a: ConceptBox, b: ConceptBox): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/**
 * 辺ラベルを、自分の経路の上で、ノードの箱にも先に置いたラベルにも重ならない最初の位置に置く。
 * 候補は経路の中ほどの区間から順に、区間の中央・その前後。どこにも置けなければ重なりの最も少ない候補。
 * 実測 2026-09-25: 同じ層の辺のラベルが狭い隙間で箱に隠れる・同じ点に重なる図が全体の約3割あった。決定論的。
 */
function placeEdgeLabels(edges: ConceptLayoutEdge[], nodes: ConceptLayoutNode[]): void {
  const nodeBoxes = nodes.map(conceptBoxOf);
  for (const edge of edges) {
    if (edge.label) continue;
    const middle = edge.points[Math.floor((edge.points.length - 1) / 2)]!;
    const next = edge.points[Math.floor((edge.points.length - 1) / 2) + 1] ?? middle;
    edge.labelAt = { x: round2((middle.x + next.x) / 2), y: round2((middle.y + next.y) / 2) };
  }
  // 置ける場所の少ないラベル（短い直線の辺など）から先に置く。長い経路のラベルが先に場所を取ると、
  // 後から置く短い辺のラベルが行き場を失って重なる。同数なら辺の並び順。
  const labeled = edges
    .map((edge, order) => ({ edge, order, width: edge.label ? edgeLabelWidth(edge.label) : 0 }))
    .filter((item) => item.edge.label)
    .map((item) => ({
      ...item,
      candidates: labelCandidates(item.edge.points, item.width),
    }))
    .map((item) => ({
      ...item,
      free: item.candidates.filter((point) => nodeBoxes.every((box) => !boxesOverlap(labelBoxAt(point, item.width), box))).length,
    }))
    .sort((a, b) => a.free - b.free || a.order - b.order);
  const chosen: Array<ConceptBox | undefined> = labeled.map(() => undefined);
  // 重み: 箱に重なる 10、他のラベルに重なる 3、他の辺の線がラベルを横切る 1（読めるが読みにくい）。
  const crossings = (index: number, box: ConceptBox): number => edges
    .filter((edge) => edge !== labeled[index]!.edge)
    .reduce((count, edge) => count + edge.points.slice(1)
      .filter((point, at) => segmentCrossesBox(edge.points[at]!, point, box)).length, 0);
  const costAt = (index: number, point: ConceptPoint): number => {
    const box = labelBoxAt(point, labeled[index]!.width);
    return nodeBoxes.filter((node) => boxesOverlap(box, node)).length * 10
      + chosen.filter((other, at) => at !== index && other !== undefined && boxesOverlap(box, other)).length * 3
      + crossings(index, box);
  };
  const place = (index: number): boolean => {
    const item = labeled[index]!;
    let best: { point: ConceptPoint; cost: number } | undefined;
    for (const point of item.candidates) {
      const cost = costAt(index, point);
      if (!best || cost < best.cost) best = { point, cost };
      if (cost === 0) break;
    }
    const moved = item.edge.labelAt.x !== best!.point.x || item.edge.labelAt.y !== best!.point.y || chosen[index] === undefined;
    item.edge.labelAt = best!.point;
    chosen[index] = labelBoxAt(best!.point, item.width);
    return moved;
  };
  labeled.forEach((_, index) => place(index));
  // 重なりが残ったら、重なっている2つのラベルの置き場所を組み合わせで探し直す（数回まで）。
  // 片方ずつ動かすと、先に中央を取ったラベルが動けず解けない配置がある。
  for (let round = 0; round < 6; round += 1) {
    let changed = false;
    for (let i = 0; i < labeled.length; i += 1) {
      for (let j = i + 1; j < labeled.length; j += 1) {
        if (!boxesOverlap(chosen[i]!, chosen[j]!)) continue;
        const [savedI, savedJ] = [chosen[i], chosen[j]];
        chosen[i] = undefined;
        chosen[j] = undefined;
        let best: { a: ConceptPoint; b: ConceptPoint; cost: number } | undefined;
        for (const a of labeled[i]!.candidates) {
          const costA = costAt(i, a);
          if (best && costA >= best.cost) continue;
          const boxA = labelBoxAt(a, labeled[i]!.width);
          for (const b of labeled[j]!.candidates) {
            const cost = costA + costAt(j, b) + (boxesOverlap(boxA, labelBoxAt(b, labeled[j]!.width)) ? 3 : 0);
            if (!best || cost < best.cost) best = { a, b, cost };
            if (best.cost === 0) break;
          }
          if (best?.cost === 0) break;
        }
        const before = (savedI && savedJ) ? costAtPair(i, savedI, j, savedJ) : Infinity;
        if (best && best.cost < before) {
          labeled[i]!.edge.labelAt = best.a;
          labeled[j]!.edge.labelAt = best.b;
          chosen[i] = labelBoxAt(best.a, labeled[i]!.width);
          chosen[j] = labelBoxAt(best.b, labeled[j]!.width);
          changed = true;
        } else {
          chosen[i] = savedI;
          chosen[j] = savedJ;
        }
      }
    }
    if (!changed) break;
  }

  function costAtPair(i: number, a: ConceptBox, j: number, b: ConceptBox): number {
    const against = (index: number, box: ConceptBox): number => nodeBoxes.filter((node) => boxesOverlap(box, node)).length * 10
      + chosen.filter((other) => other !== undefined && boxesOverlap(box, other)).length * 3
      + crossings(index, box);
    return against(i, a) + against(j, b) + (boxesOverlap(a, b) ? 3 : 0);
  }
}

/** 外側の迂回路やラベルが余白からはみ出したときだけ、全体をずらしてキャンバスを広げる。収まっている図は座標を変えない。 */
function fitConceptCanvas(layout: ConceptLayout, margin: number): ConceptLayout {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const node of layout.nodes) {
    xs.push(node.x - node.width / 2, node.x + node.width / 2);
    ys.push(node.y - node.height / 2, node.y + node.height / 2);
  }
  for (const edge of layout.edges) {
    for (const point of edge.points) {
      xs.push(point.x);
      ys.push(point.y);
    }
    if (edge.label) {
      const box = labelBoxAt(edge.labelAt, edgeLabelWidth(edge.label));
      xs.push(box.left, box.right);
      ys.push(box.top, box.bottom);
    }
  }
  const dx = Math.max(0, margin - Math.min(...xs));
  const dy = Math.max(0, margin - Math.min(...ys));
  const width = round2(Math.max(layout.width + dx, Math.max(...xs) + dx + margin));
  const height = round2(Math.max(layout.height + dy, Math.max(...ys) + dy + margin));
  if (dx === 0 && dy === 0 && width === layout.width && height === layout.height) return layout;
  const shift = (point: ConceptPoint): ConceptPoint => ({ x: round2(point.x + dx), y: round2(point.y + dy) });
  return {
    ...layout,
    width,
    height,
    nodes: layout.nodes.map((node) => ({ ...node, x: round2(node.x + dx), y: round2(node.y + dy) })),
    edges: layout.edges.map((edge) => ({ ...edge, points: edge.points.map(shift), labelAt: shift(edge.labelAt) })),
  };
}

/**
 * 器の幅から向きを決める。狭い器では縦。広くても、横並びの図が読める大きさまで縮められないなら縦。
 * 純関数にしてあるのは、境目の判定を DOM なしで確かめるため。
 */
export function chooseConceptOrientation(containerWidth: number, horizontalWidth: number): ConceptDiagramOrientation {
  if (containerWidth < NARROW_LAYOUT_MAX_WIDTH) return 'vertical';
  return horizontalWidth * CONCEPT_MIN_SCALE <= containerWidth ? 'horizontal' : 'vertical';
}

function conceptCanvas(diagram: ConceptDiagram, layout: ConceptLayout): SVGElement {
  const labelOf = new Map(diagram.nodes.map((node) => [node.id, node.label]));
  const relations = layout.edges.map(
    (edge) => `${labelOf.get(edge.from) ?? edge.from}→${labelOf.get(edge.to) ?? edge.to}${edge.label ? `（${edge.label}）` : ''}`,
  );
  const canvas = svg('svg', {
    class: `concept-diagram__canvas concept-diagram__canvas--${layout.orientation}`,
    viewBox: `0 0 ${layout.width} ${layout.height}`,
    width: layout.width,
    height: layout.height,
    style: `max-width:${layout.width}px`,
    role: 'img',
    'aria-label':
      `${diagram.title}。要素: ${layout.nodes.map((node) => `${labelOf.get(node.id) ?? node.id}（${CONCEPT_ROLE_LABELS[node.role]}）`).join('、')}。` +
      (relations.length > 0 ? `関係: ${relations.join('、')}。` : ''),
  });
  const defs = svg('defs');
  const marker = svg('marker', {
    id: 'ps-cd-arrow',
    viewBox: '0 0 10 10',
    refX: '9',
    refY: '5',
    markerWidth: '6',
    markerHeight: '6',
    orient: 'auto',
  });
  marker.appendChild(svg('path', { d: 'M 0 0 L 10 5 L 0 10 z', class: 'concept-diagram__arrow' }));
  defs.appendChild(marker);
  canvas.appendChild(defs);

  const edgeLayer = svg('g');
  const nodeLayer = svg('g');
  canvas.appendChild(edgeLayer);
  canvas.appendChild(nodeLayer);

  for (const edge of layout.edges) {
    edgeLayer.appendChild(
      svg('polyline', {
        class: 'concept-diagram__edge',
        points: edge.points.map((point) => `${point.x},${point.y}`).join(' '),
        fill: 'none',
        'stroke-linejoin': 'round',
        'marker-end': 'url(#ps-cd-arrow)',
      }),
    );
    if (edge.label) {
      edgeLayer.appendChild(
        svg(
          'text',
          { class: 'concept-diagram__edge-label', x: edge.labelAt.x, y: edge.labelAt.y - 4, 'text-anchor': 'middle' },
          edge.label,
        ),
      );
    }
  }

  const noteOf = new Map(diagram.nodes.map((node) => [node.id, node.note]));
  for (const node of layout.nodes) {
    const group = svg('g', { class: `concept-diagram__node concept-diagram__node--${node.role}` });
    const label = labelOf.get(node.id) ?? node.id;
    const note = noteOf.get(node.id);
    group.appendChild(svg('title', {}, `${CONCEPT_ROLE_LABELS[node.role]}：${label}${note ? `。${note}` : ''}`));
    const left = node.x - node.width / 2;
    const top = node.y - node.height / 2;
    group.appendChild(svg('rect', { class: 'concept-diagram__box', x: left, y: top, width: node.width, height: node.height, rx: 10 }));
    group.appendChild(svg('text', { class: 'concept-diagram__role', x: left + 10, y: top + 15 }, CONCEPT_ROLE_LABELS[node.role]));
    // ラベルは役割行の下の領域で上下中央に置く。行数が少ないノードだけ上に詰まらないようにする。
    const maxLines = (node.height - CONCEPT_NODE_CHROME) / CONCEPT_LINE_HEIGHT;
    const firstBaseline = top + 26 + ((maxLines - node.lines.length) * CONCEPT_LINE_HEIGHT) / 2 + 11;
    const text = svg('text', { class: 'concept-diagram__label', x: node.x, y: firstBaseline, 'text-anchor': 'middle' });
    node.lines.forEach((line, index) => {
      text.appendChild(svg('tspan', { x: node.x, dy: index === 0 ? 0 : CONCEPT_LINE_HEIGHT }, line));
    });
    group.appendChild(text);
    nodeLayer.appendChild(group);
  }
  return canvas;
}

/**
 * 概念図を描く。呼び出し側は isPublishableConceptDiagram を通った図だけを渡す。
 * 器の幅に応じて横並び／縦積みを切り替える（取り付け後は ResizeObserver で追従）。
 */
export function renderConceptDiagram(diagram: ConceptDiagram): HTMLElement {
  const figure = el('figure', {
    class: 'concept-diagram',
    'aria-label': `${diagram.title}。${diagram.caption}`,
  });
  figure.appendChild(
    el(
      'div',
      { class: 'concept-diagram__head' },
      el('span', { class: 'visual-summary__eyebrow' }, 'HOW IT WORKS'),
      el('h2', {}, diagram.title),
      el('p', {}, '原論文に書かれている要素と関係だけを図にしました。色と役割名で要素の種類を示します。'),
    ),
  );
  const host = el('div', { class: 'concept-diagram__host' });
  figure.appendChild(host);

  const horizontalWidth = layoutConceptDiagram(diagram, 'horizontal').width;
  let drawn: ConceptDiagramOrientation | null = null;
  const draw = (containerWidth: number): void => {
    const orientation = chooseConceptOrientation(containerWidth, horizontalWidth);
    if (orientation === drawn) return;
    drawn = orientation;
    clear(host);
    host.appendChild(conceptCanvas(diagram, layoutConceptDiagram(diagram, orientation)));
  };
  // 取り付け前は器の幅が測れないので文書の幅で始め、取り付け後に器の幅へ追従する。
  draw(document.documentElement.clientWidth);
  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width > 0) draw(width);
    });
    observer.observe(figure);
  }

  const roles = Array.from(new Set(diagram.nodes.map((node) => node.role))).sort(
    (a, b) => CONCEPT_ROLE_ORDER[a] - CONCEPT_ROLE_ORDER[b] || a.localeCompare(b),
  );
  figure.appendChild(
    el(
      'ul',
      { class: 'concept-diagram__legend', 'aria-label': '要素の種類' },
      ...roles.map((role) =>
        el(
          'li',
          { class: `concept-diagram__legend-item concept-diagram__legend-item--${role}` },
          el('span', { class: 'concept-diagram__swatch', 'aria-hidden': 'true' }),
          CONCEPT_ROLE_LABELS[role],
        ),
      ),
    ),
  );

  const notes = diagram.nodes.filter((node) => node.note && node.note.trim().length > 0);
  if (notes.length > 0) {
    const list = el('dl', { class: 'concept-diagram__notes' });
    for (const node of notes) {
      list.appendChild(el('dt', {}, node.label));
      list.appendChild(el('dd', {}, node.note ?? ''));
    }
    figure.appendChild(list);
  }

  const confidence = CONFIDENCE_LABEL[diagram.confidence];
  figure.appendChild(
    el(
      'figcaption',
      { class: 'concept-diagram__caption' },
      el('p', {}, diagram.caption),
      el(
        'span',
        { class: 'concept-diagram__meta' },
        `${PROVENANCE_LABELS[diagram.provenance]}${confidence ? `・${confidence}` : ''}`,
      ),
    ),
  );
  const sources = sourcesPanel(diagram.nodes.flatMap((node) => node.sources));
  if (sources) figure.appendChild(sources);
  return figure;
}

export function renderOriginalFigure(
  paper: Paper,
  explanation: ContentBlock[],
  equations: Equation[],
  options: BlockRenderOptions = {},
): HTMLElement | null {
  const info = originalFigureFor(paper);
  if (!info || explanation.length === 0) return null;
  const firstExplanation = explanation.find((block) => displayBlockText(block).trim().length > 0);
  if (!firstExplanation) return null;
  const figure = el('figure', { class: 'source-figure', 'aria-labelledby': `figure-title-${paper.id}` });
  const head = el(
    'div',
    { class: 'source-figure__head' },
    el('span', { class: 'visual-summary__eyebrow' }, 'READ THE FIGURE'),
    el('h2', { id: `figure-title-${paper.id}` }, 'Figure 1を一緒に読む'),
  );
  figure.appendChild(head);
  const image = el('img', {
    src: info.imageUrl,
    alt: `原論文 Figure 1。${displayBlockText(firstExplanation)}`,
    loading: 'lazy',
    referrerpolicy: 'no-referrer',
  });
  image.addEventListener('error', () => {
    image.remove();
    head.appendChild(el('p', { class: 'muted' }, '画像を表示できませんでした。下の原論文リンクで確認できます。'));
  });
  figure.appendChild(image);
  const guide = el('div', { class: 'source-figure__guide' });
  for (const block of explanation) {
    const rendered = renderBlock(block, equations, { ...options, paper });
    if (rendered) guide.appendChild(rendered);
  }
  figure.appendChild(guide);
  figure.appendChild(
    el(
      'figcaption',
      {},
      el('a', { href: info.sourceUrl, target: '_blank', rel: 'noopener noreferrer' }, '原論文のFigure 1と正式な説明を開く'),
      el('span', { class: 'muted' }, `（${paper.license.label}／画像は原論文サイトから表示）`),
    ),
  );
  return figure;
}

declare global {
  interface Window {
    katex?: { renderToString(tex: string, options: Record<string, unknown>): string };
  }
}

/** KaTeX で数式を描画する。読み込めていない場合は元の LaTeX をそのまま見せる。 */
export function renderMath(latex: string, displayMode: boolean): HTMLElement {
  const container = el('div', { class: displayMode ? 'equation__render' : '' });
  const katex = window.katex;
  if (!katex) {
    container.appendChild(el('code', {}, latex));
    return container;
  }
  try {
    container.innerHTML = katex.renderToString(latex, {
      displayMode,
      throwOnError: false,
      strict: 'ignore',
      output: 'html',
    });
  } catch {
    container.appendChild(el('code', {}, latex));
  }
  return container;
}

type RichText = (text: string) => DocumentFragment;

interface BlockRenderOptions {
  paper?: Paper;
  richText?: RichText;
}

function primaryPaperUrl(paper: Paper | undefined): string | undefined {
  return paper?.urls.html ?? paper?.urls.pdf ?? paper?.urls.landing;
}

/**
 * 原文引用に残る抽出由来のゼロ幅文字（U+200B など）は表示するときだけ落とす。
 * 保存値は原文スナップショットとの照合に使うので変えない（実測 2026-09-25: 5論文で「360 ° 」のような空きが出ていた）。
 */
export function displayQuote(quote: string): string {
  // 両側の空白に挟まれていた場合は、消したあとに空白が2つ並ばないよう1つにまとめる（それ以外の空白は原文どおり）。
  return quote.replace(/( ?)[\u200B-\u200D\uFEFF]+( ?)/g, (_match, before: string, after: string) => (before || after ? ' ' : ''));
}

function sourceRow(source: SourceRef, paper?: Paper): HTMLElement {
  if (source.kind === 'paper') {
    const label = source.section ? `原文（${source.section}）より引用` : '原文より引用';
    const href = primaryPaperUrl(paper);
    return el(
      'div',
      {},
      href
        ? el('a', { href, target: '_blank', rel: 'noopener noreferrer' }, `${label} ↗`)
        : el('span', { class: 'muted' }, label),
      el('blockquote', { class: 'block__quote' }, displayQuote(source.quote)),
    );
  }
  return el(
    'div',
    {},
    el('a', { href: source.url, target: '_blank', rel: 'noopener noreferrer' }, `${source.title} ↗`),
    el('span', { class: 'muted' }, `（${source.publisher}／確認日 ${source.checkedAt}）`),
    source.quote ? el('blockquote', { class: 'block__quote' }, displayQuote(source.quote)) : null,
  );
}

function sourcesPanel(sources: SourceRef[], paper?: Paper): HTMLElement | null {
  if (sources.length === 0) return null;
  const details = el('details', { class: 'block__sources' });
  details.appendChild(el('summary', {}, `出典を表示（${sources.length}件）`));
  for (const source of sources) details.appendChild(sourceRow(source, paper));
  return details;
}

function sourceLinks(sources: SourceRef[], paper?: Paper): HTMLElement | null {
  if (sources.length === 0) return null;
  const row = el('span', { class: 'source-links' });
  const seen = new Set<string>();
  for (const source of sources) {
    const key = source.kind === 'paper'
      ? `paper:${source.snapshotId}:${source.section ?? ''}`
      : `external:${source.url}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (source.kind === 'paper') {
      const href = primaryPaperUrl(paper);
      const label = source.section ? `原文 ${source.section}` : '原文';
      row.appendChild(href
        ? el('a', { href, target: '_blank', rel: 'noopener noreferrer', title: displayQuote(source.quote) }, `${label} ↗`)
        : el('span', { title: displayQuote(source.quote) }, label));
    } else {
      row.appendChild(
        el('a', { href: source.url, target: '_blank', rel: 'noopener noreferrer' }, `${source.publisher} ↗`),
      );
    }
  }
  return row.childElementCount > 0 ? row : null;
}

function provenanceBadge(block: Pick<ContentBlock, 'provenance' | 'confidence'>): HTMLElement {
  const confidence = CONFIDENCE_LABEL[block.confidence];
  return el(
    'span',
    { class: `provenance provenance--${block.provenance}` },
    el('span', { 'aria-hidden': 'true' }, PROVENANCE_ICON[block.provenance]),
    PROVENANCE_LABELS[block.provenance],
    confidence ? `・${confidence}` : '',
  );
}

function richNode(tag: 'p' | 'li' | 'td' | 'th', text: string, richText?: RichText): HTMLElement {
  const node = el(tag, {});
  node.appendChild(richText ? richText(text) : document.createTextNode(text));
  return node;
}

export function renderBlock(
  block: ContentBlock,
  equations: Equation[],
  options: BlockRenderOptions = {},
): HTMLElement | null {
  if (block.kind === 'equation') {
    const equation = equations.find((e) => e.id === block.equationId);
    return equation ? renderEquation(equation, options.paper) : null;
  }

  if (block.kind === 'takeaways' || isUnhelpfulEditorialNote(block)) return null;
  const blockText = displayBlockText(block).trim();
  const blockItems = displayBlockItems(block);

  if (block.kind === 'heading') {
    if (!blockText) return null;
    const level = block.headingLevel === 3 ? 'h3' : 'h2';
    return el(level, { id: `section-${block.id}`, class: 'heading-block' }, blockText);
  }

  const wrapper = el('div', { class: `block block--${block.provenance}${block.kind === 'callout' ? ' block--callout' : ''}` });
  wrapper.appendChild(provenanceBadge(block));

  const body = el('div', { class: 'block__body' });
  if (block.kind === 'list') {
    if (blockItems.length === 0) return null;
    const list = el('ul', {});
    for (const item of blockItems) list.appendChild(richNode('li', item, options.richText));
    body.appendChild(list);
  } else if (block.kind === 'table') {
    const scroller = el('div', {
      class: 'table-scroll',
      role: 'region',
      tabindex: '0',
      'aria-label': '論文中の比較表',
    });
    const table = el('table', { class: 'equation__symbols' });
    (block.rows ?? []).forEach((row, index) => {
      const tr = el('tr', {});
      for (const cell of row) tr.appendChild(richNode(index === 0 ? 'th' : 'td', cell, options.richText));
      table.appendChild(tr);
    });
    scroller.appendChild(table);
    body.appendChild(scroller);
  } else {
    for (const paragraph of blockText.split(/\n{2,}/)) {
      if (paragraph.trim().length === 0) continue;
      body.appendChild(richNode('p', paragraph, options.richText));
    }
  }
  wrapper.appendChild(body);

  const sources = sourcesPanel(block.sources, options.paper);
  if (sources) wrapper.appendChild(sources);
  return wrapper;
}

export function renderEquation(equation: Equation, paper?: Paper): HTMLElement {
  const wrapper = el('section', { class: 'equation', 'aria-label': `数式 ${equation.id}` });

  if (equation.latex) {
    wrapper.appendChild(renderMath(equation.latex, true));
  } else {
    wrapper.appendChild(
      el(
        'p',
        { class: 'equation__missing' },
        '要確認：原文からこの数式を抽出できませんでした。推測での復元は行っていません。原文を直接ご確認ください。',
      ),
    );
  }

  if (equation.symbols.length > 0) {
    const table = el('table', { class: 'equation__symbols' });
    table.appendChild(
      el('tr', {}, el('th', { scope: 'col' }, '記号'), el('th', { scope: 'col' }, '意味'), el('th', { scope: 'col' }, '単位')),
    );
    for (const symbol of equation.symbols) {
      table.appendChild(
        el(
          'tr',
          {},
          el('td', {}, renderMath(symbol.symbol, false)),
          el('td', {}, symbol.meaning),
          el('td', {}, symbol.unit ?? '—'),
        ),
      );
    }
    wrapper.appendChild(table);
  }

  const list = el('dl', { class: 'equation__level' });
  list.appendChild(el('dt', {}, '論文内での役割'));
  list.appendChild(el('dd', {}, equation.role));
  if (equation.assumptions.length > 0) {
    list.appendChild(el('dt', {}, '成立する前提'));
    list.appendChild(el('dd', {}, equation.assumptions.join(' / ')));
  }
  list.appendChild(el('dt', {}, '直感的な意味'));
  list.appendChild(el('dd', {}, equation.intuition));
  wrapper.appendChild(list);

  const sources = sourcesPanel(equation.sources, paper);
  if (sources) wrapper.appendChild(sources);
  return wrapper;
}

function regexpEscape(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 用語の照合用に、ハイフン類と全角英数記号を1文字ずつ半角へ寄せる。1文字対1文字の置き換えなので、
 * 寄せた文字列で見つけた位置をそのまま元の文字列に当てられる（画面には原文の字形のまま出す）。
 * 公開前の検査（src/core/verify.ts の normalizeForMatch）はこの揺れを同じ語とみなすので、Web でも押せるようにする
 * （実測 2026-09-25: 用語「Ni\u2010ZnS\u2010Sv」と本文「Ni-ZnS-Sv」のように、2論文3語が押せなかった）。
 */
function foldForGlossary(value: string): string {
  return value
    .replace(/[\u2010-\u2015\u2212\uFE58\uFE63]/g, '-')
    .replace(/[\uFF01-\uFF5E]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0xFEE0))
    .replace(/\u3000/g, ' ');
}

interface InlineGlossaryRenderer {
  popover: HTMLElement;
  richText: RichText;
  /** 本文を描き終えた後に呼ぶ。通常の照合で1か所も押せなかった用語だけを、埋め込み箇所で押せるようにする。 */
  linkUnreachableTerms(): void;
  close(): void;
  ownsFocus(target: EventTarget | null): boolean;
}

export interface GlossaryMatcher {
  pattern: RegExp;
  byAlias: Map<string, InlineGlossaryEntry>;
}

/** 用語と別表記から照合器を作る。長い表記を先に試し、短い語が長い語の一部を奪わないようにする。 */
export function buildGlossaryMatcher(entries: InlineGlossaryEntry[] | undefined): GlossaryMatcher | null {
  const aliases = new Map<string, { alias: string; entry: InlineGlossaryEntry }>();
  for (const entry of entries ?? []) {
    for (const alias of [entry.term, ...entry.aliases]) {
      const value = foldForGlossary(alias.trim());
      const key = value.toLowerCase();
      if (value.length > 0 && !aliases.has(key)) aliases.set(key, { alias: value, entry });
    }
  }
  const ordered = [...aliases.values()].sort((a, b) =>
    b.alias.length - a.alias.length || (a.alias < b.alias ? -1 : a.alias > b.alias ? 1 : 0));
  if (ordered.length === 0) return null;
  return {
    byAlias: new Map(ordered.map((item) => [item.alias.toLowerCase(), item.entry])),
    pattern: new RegExp(ordered.map((item) => regexpEscape(item.alias)).join('|'), 'giu'),
  };
}

/** 本文の中で押せる用語の位置（DOM を使わない）。start/end は元の文字列の位置で、表示には元の字形を使う。 */
export function findGlossaryTerms(
  text: string,
  matcher: GlossaryMatcher,
): Array<{ start: number; end: number; entry: InlineGlossaryEntry }> {
  const found: Array<{ start: number; end: number; entry: InlineGlossaryEntry }> = [];
  const folded = foldForGlossary(text);
  for (const match of folded.matchAll(matcher.pattern)) {
    const at = match.index ?? 0;
    const key = match[0];
    // 英字だけの語が英単語の途中に埋まっているとき（rFID の FID など）は押せる語にしない。
    if (/^[A-Za-z]+$/.test(key)
      && /[A-Za-z]/.test((folded[at - 1] ?? '') + (folded[at + key.length] ?? ''))) continue;
    const entry = matcher.byAlias.get(key.toLowerCase());
    if (entry) found.push({ start: at, end: at + key.length, entry });
  }
  return found;
}

/**
 * 通常の照合で1か所も押せなかった用語の、最初の埋め込み箇所。英単語の途中（rFID の FID、PDBe の PDB など）も
 * 候補にするが、大文字小文字まで同じ表記に限る。texts はボタンにしなかった本文の断片を表示順に並べたもので、
 * 別の用語のボタンの中（長い用語の一部）は対象にしない。見つからなければ null。
 *
 * 公開前検査は用語か別表記が本文に現れることを求めるが、画面は英単語の途中を押せる語にしない。そのままだと
 * 検査を通った用語説明が一度も開けない（実測 2026-09-25: 12,380語中15語。うち13語がこの形）。
 */
export function findEmbeddedGlossaryTerm(
  texts: readonly string[],
  entry: InlineGlossaryEntry,
): { index: number; start: number; end: number } | null {
  const surfaces = [entry.term, ...entry.aliases]
    .map((alias) => foldForGlossary(alias.trim()))
    .filter((surface) => surface.length > 0);
  for (let index = 0; index < texts.length; index += 1) {
    const folded = foldForGlossary(texts[index] ?? '');
    let best: { start: number; end: number } | null = null;
    for (const surface of surfaces) {
      const at = folded.indexOf(surface);
      if (at < 0) continue;
      // 同じ位置なら長い表記を採る（短い表記が長い表記の一部だけを押せる語にしない）。
      if (best === null || at < best.start || (at === best.start && at + surface.length > best.end)) {
        best = { start: at, end: at + surface.length };
      }
    }
    if (best) return { index, ...best };
  }
  return null;
}

function inlineGlossaryRenderer(
  entries: InlineGlossaryEntry[] | undefined,
  paper: Paper | undefined,
  assetId: string,
): InlineGlossaryRenderer | null {
  const matcher = buildGlossaryMatcher(entries);
  if (!matcher) return null;
  const popoverId = `term-help-${assetId}`;
  const popover = el('aside', {
    id: popoverId,
    class: 'term-help',
    popover: 'auto',
    role: 'dialog',
    'aria-label': '用語の説明',
  });
  let active: HTMLButtonElement | undefined;

  const close = (): void => {
    if (popover.matches(':popover-open')) popover.hidePopover();
    active?.setAttribute('aria-expanded', 'false');
    active = undefined;
  };
  const open = (button: HTMLButtonElement, entry: InlineGlossaryEntry): void => {
    if (active === button && popover.matches(':popover-open')) {
      close();
      return;
    }
    active?.setAttribute('aria-expanded', 'false');
    active = button;
    button.setAttribute('aria-expanded', 'true');
    const dismiss = el('button', { class: 'term-help__close', type: 'button', 'aria-label': '用語の説明を閉じる' }, '×');
    dismiss.addEventListener('click', () => {
      close();
      button.focus();
    });
    const meta = el('div', { class: 'term-help__meta' }, provenanceBadge(entry));
    const links = sourceLinks(entry.sources, paper);
    if (links) meta.appendChild(links);
    popover.replaceChildren(
      dismiss,
      el('span', { class: 'visual-summary__eyebrow' }, '言葉の意味'),
      el('h3', {}, entry.term),
      el('p', {}, entry.definition),
      el('p', { class: 'term-help__context' }, entry.context),
      meta,
    );
    if (!popover.matches(':popover-open')) popover.showPopover();
    const rect = button.getBoundingClientRect();
    const width = Math.min(360, window.innerWidth - 32);
    popover.style.width = `${width}px`;
    popover.style.left = `${Math.min(Math.max(16, rect.left), window.innerWidth - width - 16)}px`;
    const height = popover.getBoundingClientRect().height;
    popover.style.top = `${Math.max(12, Math.min(rect.bottom + 10, window.innerHeight - height - 16))}px`;
  };
  popover.addEventListener('toggle', () => {
    if (!popover.matches(':popover-open')) {
      active?.setAttribute('aria-expanded', 'false');
      active = undefined;
    }
  });

  // 押せる語にできた用語と、ボタンにしなかった本文の断片（表示順）。後から埋め込み箇所を探すのに使う。
  const reached = new Set<InlineGlossaryEntry>();
  const plainNodes: Text[] = [];
  const termButton = (matched: string, entry: InlineGlossaryEntry): HTMLButtonElement => {
    const button = el('button', {
      class: 'term',
      type: 'button',
      'aria-expanded': 'false',
      'aria-controls': popoverId,
      'aria-haspopup': 'dialog',
      'aria-label': `${matched}：意味を確認`,
    }, matched);
    button.addEventListener('click', () => open(button, entry));
    reached.add(entry);
    return button;
  };
  const plain = (value: string): Text => {
    const node = document.createTextNode(value);
    plainNodes.push(node);
    return node;
  };

  const richText = (text: string): DocumentFragment => {
    const fragment = document.createDocumentFragment();
    let position = 0;
    for (const { start: at, end, entry } of findGlossaryTerms(text, matcher)) {
      // ボタンの文字は原文の字形のまま出す（寄せたのは照合だけ）。
      const matched = text.slice(at, end);
      fragment.appendChild(plain(text.slice(position, at)));
      fragment.appendChild(termButton(matched, entry));
      position = at + matched.length;
    }
    fragment.appendChild(plain(text.slice(position)));
    return fragment;
  };
  const linkUnreachableTerms = (): void => {
    for (const entry of entries ?? []) {
      if (reached.has(entry)) continue;
      const hit = findEmbeddedGlossaryTerm(plainNodes.map((node) => node.data), entry);
      if (!hit) continue;
      // 寄せは1文字対1文字なので、見つけた位置をそのまま元の文字列へ当てられる。
      const node = plainNodes[hit.index]!;
      const after = node.splitText(hit.end);
      const middle = node.splitText(hit.start);
      middle.replaceWith(termButton(middle.data, entry));
      plainNodes.splice(hit.index, 1, node, after);
    }
  };
  const ownsFocus = (target: EventTarget | null): boolean =>
    target === active || (target instanceof Node && popover.contains(target));
  return { popover, richText, linkUnreachableTerms, close, ownsFocus };
}

const HIDDEN_LEGACY_HEADINGS = new Set([
  '先に知る言葉',
  '用語の定義',
  'この論文がいちばん伝えたいこと',
  '自分で説明できる？',
]);

/** 旧データは読めるまま残し、現行画面では重複する要約・用語章・quizを再掲しない。 */
function visibleBodyBlocks(blocks: ContentBlock[]): ContentBlock[] {
  const visible: ContentBlock[] = [];
  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index]!;
    if (block.kind === 'takeaways' || isVisualSummaryText(block.text) || isLegacyOutline(block.text)) continue;
    if (block.kind === 'heading' && HIDDEN_LEGACY_HEADINGS.has(block.text?.trim() ?? '')) {
      while (index + 1 < blocks.length && blocks[index + 1]?.kind !== 'heading') index += 1;
      continue;
    }
    if (block.kind === 'heading' && blocks[index + 1]?.kind === 'takeaways') {
      index += 1;
      continue;
    }
    visible.push(block);
  }
  return visible;
}

function renderOverview(
  items: ReturnType<typeof reportOverviewItems>,
  options: BlockRenderOptions,
  assetId: string,
): HTMLElement | null {
  if (items.length === 0) return null;
  const titleId = `report-overview-${assetId}`;
  const section = el('section', { class: 'report-overview', 'aria-labelledby': titleId });
  section.appendChild(el('h2', { id: titleId }, '研究の要点'));
  const grid = el('div', { class: 'report-overview__grid' });
  items.forEach((item, index) => {
    const text = el('p', {});
    text.appendChild(options.richText ? options.richText(item.text) : document.createTextNode(item.text));
    const meta = el('div', { class: 'report-overview__meta' }, provenanceBadge(item));
    const links = sourceLinks(item.sources, options.paper);
    if (links) meta.appendChild(links);
    grid.appendChild(
      el(
        'article',
        { class: 'report-overview__item' },
        el('span', { class: 'report-overview__number', 'aria-hidden': 'true' }, String(index + 1).padStart(2, '0')),
        el('h3', {}, item.label),
        text,
        meta,
      ),
    );
  });
  section.append(grid, el('p', { class: 'report-overview__note' }, '詳しい条件と意味は本文で確認できます。'));
  return section;
}

function appendRenderedBlocks(host: HTMLElement, blocks: ContentBlock[], equations: Equation[], options: BlockRenderOptions): void {
  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index]!;
    if (block.kind === 'heading' && /^(?:Figure 1を一緒に読む|原論文の重要図)$/.test(block.text?.trim() ?? '')) {
      let end = index + 1;
      while (end < blocks.length && blocks[end]?.kind !== 'heading') end += 1;
      const explanation = blocks.slice(index + 1, end);
      const figure = options.paper ? renderOriginalFigure(options.paper, explanation, equations, options) : null;
      if (figure) host.appendChild(figure);
      else {
        const heading = renderBlock(block, equations, options);
        if (heading) host.appendChild(heading);
        appendRenderedBlocks(host, explanation, equations, options);
      }
      index = end - 1;
      continue;
    }
    const rendered = renderBlock(block, equations, options);
    if (rendered) host.appendChild(rendered);
  }
}

function renderReadingBody(blocks: ContentBlock[], equations: Equation[], options: BlockRenderOptions): HTMLElement {
  const starts = REPORT_SECTION_HEADINGS.map((heading) =>
    blocks.findIndex((block) => block.kind === 'heading' && block.text?.trim() === heading));
  const complete = starts.every((index) => index >= 0)
    && starts.every((index, position) => position === 0 || index > starts[position - 1]!);
  if (!complete) {
    const legacy = el('article', { class: 'reading-body reading-body--legacy' });
    appendRenderedBlocks(legacy, blocks, equations, options);
    return legacy;
  }

  const layout = el('div', { class: 'reading-layout' });
  const aside = el('aside', { class: 'reader-toc' });
  const nav = el('nav', { 'aria-label': '本文の目次' }, el('p', { class: 'reader-toc__eyebrow' }, 'この論文を読み解く'));
  REPORT_SECTION_HEADINGS.forEach((heading, index) => {
    const block = blocks[starts[index]!]!;
    nav.appendChild(el('a', { href: `#section-${block.id}` }, `${String(index + 1).padStart(2, '0')}  ${heading}`));
  });
  aside.append(nav, el('p', { class: 'reader-toc__hint' }, '点線の言葉を選ぶと、その場で意味を確認できます。'));

  const body = el('article', { class: 'reading-body' });
  if (starts[0]! > 0) appendRenderedBlocks(body, blocks.slice(0, starts[0]), equations, options);
  REPORT_SECTION_HEADINGS.forEach((heading, index) => {
    const start = starts[index]!;
    const end = starts[index + 1] ?? blocks.length;
    const headingBlock = blocks[start]!;
    const chapter = el(
      'section',
      { class: 'report-chapter', id: `section-${headingBlock.id}`, 'aria-labelledby': `section-title-${headingBlock.id}` },
      el('p', { class: 'report-chapter__step' }, `${String(index + 1).padStart(2, '0')} / ${String(REPORT_SECTION_HEADINGS.length).padStart(2, '0')}`),
      el('h2', { id: `section-title-${headingBlock.id}` }, heading),
    );
    appendRenderedBlocks(chapter, blocks.slice(start + 1, end), equations, options);
    body.appendChild(chapter);
  });
  layout.append(aside, body);
  return layout;
}

export function renderTextAsset(asset: ContentAsset, paper?: Paper): HTMLElement {
  const container = el('div', {});

  // 本文が無い＝まだ生成できていない。空白のまま見せず、理由と不足設定を示す。
  if ((asset.blocks ?? []).length === 0) {
    container.appendChild(
      el(
        'div',
        { class: 'alert alert--warn' },
        el('strong', {}, 'この解説はまだ生成されていません。'),
        ' 論文のメタデータ・選定理由・引用関係は上に表示しています。',
        asset.missingConfig.length > 0
          ? el('ul', {}, ...asset.missingConfig.map((item) => el('li', {}, item)))
          : null,
      ),
    );
    return container;
  }

  const equations = asset.equations ?? [];
  const allBlocks = asset.blocks ?? [];
  const { overview, body: reportBody } = splitReportBlocks(allBlocks);
  const blocks = visibleBodyBlocks(reportBody);
  const referenced = new Set(blocks.map((b) => b.equationId).filter(Boolean));
  const glossary = inlineGlossaryRenderer(asset.glossary, paper, asset.id);
  const options: BlockRenderOptions = { paper, ...(glossary ? { richText: glossary.richText } : {}) };

  const overviewNode = isPublishableReportOverview(allBlocks)
    ? renderOverview(overview, options, asset.id)
    : null;
  if (overviewNode) container.appendChild(overviewNode);
  // 概念図は要点の言い換えではなく、原文根拠を持つ仕組み図だけを補助的に表示する。
  if (asset.diagram && isPublishableConceptDiagram(asset.diagram)) container.appendChild(renderConceptDiagram(asset.diagram));
  container.appendChild(renderReadingBody(blocks, equations, options));
  glossary?.linkUnreachableTerms();

  // 本文から参照されていない数式も、確認できる形で末尾にまとめる。
  const orphans = equations.filter((e) => !referenced.has(e.id));
  if (orphans.length > 0) {
    const supplement = el('section', { class: 'report-supplement' }, el('h2', { class: 'heading-block' }, '数式'));
    for (const equation of orphans) supplement.appendChild(renderEquation(equation, paper));
    container.appendChild(supplement);
  }

  if (glossary) {
    container.appendChild(glossary.popover);
    container.addEventListener('focusout', (event) => {
      const next = event.relatedTarget;
      if (!glossary.ownsFocus(next)) glossary.close();
    });
  }

  return container;
}

/** 検証結果の要約。利用者に「どこまで確認できているか」を見せる。 */
export function renderVerificationSummary(asset: ContentAsset): HTMLElement | null {
  const verification = asset.verification;
  if (!verification) return null;
  const errors = verification.findings.filter((f) => f.severity === 'error');
  const warnings = verification.findings.filter((f) => f.severity === 'warning');
  if (errors.length === 0 && warnings.length === 0) {
    return el('p', { class: 'muted' }, `自動確認：指摘なし（${verification.checkedAt.slice(0, 10)} 実施）`);
  }
  const details = el('details', { class: 'block__sources' });
  details.appendChild(
    el('summary', {}, `自動確認の指摘：要修正 ${errors.length} 件／注意 ${warnings.length} 件`),
  );
  const list = el('ul', {});
  for (const finding of [...errors, ...warnings].slice(0, 20)) {
    list.appendChild(el('li', { html: `<strong>${escapeHtml(finding.checkId)}</strong>: ${escapeHtml(finding.message)}` }));
  }
  details.appendChild(list);
  return details;
}
